import React, {useEffect, useState} from 'react';
import {useServiceBase} from "../Components/ReactTable/useServiceBase";
import RetailerIdAndName from "../Components/RetailerIdAndName";
import {getRetailerName} from "../../utils/localCache/retailerMapCache";
import Page from "../Components/Page";
import {Breadcrumbs} from "../Components/Breadcrumbs";
import {LoadingWrapper} from "../Components/LoadingWrapper";
import EditableReactTableV2 from "../Components/ReactTable/EditableReactTableV2";
import {
    EditFormDateInputField,
    EditFormDropdownInputField,
    EditFormNumberInputField,
    EditFormTextInputField
} from "../Components/ReactTable/EditRecordForm";
import InactiveRowKey from "../Components/InactiveRowKey";
import DownloadCsvFromBackendServiceButton from "../../utils/components/DownloadCsvFromBackendServiceButton";
import {setValuesToNullIfEmptyOrUndefined} from "../Components/ReactTable/helpers";
import IconImage, {Icons} from "../Components/IconImage";
import {AddressMapInputField} from "./StoreService/AddressMapInputField";
import {AddressStateInputField} from "./StoreService/AddressStateInputField";
import {RetailerInputField} from "./StoreService/RetailerInputField";
import {BulkUploadStoresButton} from "./StoreService/BulkUploadStoresButton";
import {
    getCellForService,
    getFilterValueForService,
    YES_NO_DROPDOWN_OPTIONS
} from "../Components/ReactTable/common_dropdown_options";
import ConfirmationModal from "../Components/ConfirmationModal";
import {makeApiCallPost, makeApiCallWithoutCatch} from "../../Api/makeApiCall";
import {faIcons} from "../../utils/iconConstants";
import SelectDropdown from "../Components/Dropdowns/SelectDropdown";
import _ from "lodash";
import {toastError, toastSuccess} from "../../utils/toast";
import {toast} from "react-toastify";
import {MarketStoreDropdownField} from "./StoreService/MarketStoreDropdownField";
import {updateQueryStringForState} from "../../utils/urlBuilder";
import {getSalesforceStores, loadSalesforceStores} from "../../utils/localCache/salesforceStoresCache";
import {todayChicago} from "../Alerts/MissingData/filters";
import moment from "moment";

const DUPLICATED_STORE_DROPDOWN_OPTIONS = [
    {value: 1, label: "Yes"},
    {value: null, label: "No"}
]

const StoreService = () => {
    const endpointUrl = `${process.env.REACT_APP_EDP_API_BASE_URL}/stores/`;
    const downloadEndpointUrl = `${process.env.REACT_APP_EDP_API_BASE_URL}/stores`;
    const {
        loading,
        data,
        onRowEditConfirmed,
        onRowDeleteConfirmed,
        onBulkRowUploadConfirmed
    } = useServiceBase({endpointUrl});
    const [decodeTablesLoading, setDecodeTablesLoading] = useState(true);
    const [showMarketsModal, setShowMarketsModal] = useState(false);
    const [currentStoreForMarket, setCurrentStoreForMarket] = useState({});
    const [currentMarketsForStore, setCurrentMarketsForStore] = useState({});
    const [marketStores, setMarketStores] = useState({});
    const [markets, setMarkets] = useState([]);
    const [marketsWithTags, setMarketsWithTags] = useState([]);
    const [error, setError] = useState(false);
    const today = todayChicago();
    const [showLoadingModal, setShowLoadingModal] = useState(false);

    const customColumns = [
        {
            header: "Retailer",
            id: "retailerIdName",
            filterable: true,
            accessorKey: "retailerId",
            sticky: "left",
            size: 250,
            cell: ({row}) => <RetailerIdAndName retailerId={row.original.retailerId}/>,
            filterFn: (row, columnIds, filterValue) => !filterValue || Boolean(`${row.original.retailerId} - ${getRetailerName(row.original.retailerId)?.toLowerCase()}`.includes(filterValue.toLowerCase()))
        },
        {header: "Store Name", sticky: "left", size: 250, accessorKey: "storeName"},
        {header: "Store Id", size: 100, accessorKey: "storeId"},
        {header: "Retail Tag", accessorKey: "retailTag"},
        {header: "Store Tag", size: 100, accessorKey: "storeTag"},
        {header: "Store Code", accessorKey: "storeCode"},
        {header: "External Store Id", accessorKey: "externalStoreId"},
        {header: "Account", accessorKey: "account"},
        {header: "Banner", accessorKey: "banner"},
        {header: "Consumer Name", accessorKey: "consumerName"},
        {
            header: "Geographical Address?",
            id: "geographicalAddress",
            filterable: false,
            accessorFn: (row) => !!(row.latitude && row.longitude) ? 'yes' : 'no',
            size: 250,
            cell: ({row}) => (row.original.latitude && row.original.longitude) ? <><IconImage
                iconType={Icons.VALIDATED}/><span>({row.original.latitude}/{row.original.longitude})</span></> : <></>,
            filter: () => <></>
        },
        {header: "Addr 1", accessorKey: "addr1"},
        {header: "Addr 2", accessorKey: "addr2"},
        {header: "City", accessorKey: "city"},
        {header: "State", accessorKey: "state"},
        {header: "County", accessorKey: "county"},
        {header: "FIPS", accessorKey: "fips"},
        {header: "Country", accessorKey: "country"},
        {header: "Zip 1", accessorKey: "zip1"},
        {header: "Zip 2", accessorKey: "zip2"},
        {header: "Open Date", accessorKey: "openDate"},
        {
            header: "Closed", accessorKey: "closed",
            cell: ({getValue}) => getCellForService(getValue(), YES_NO_DROPDOWN_OPTIONS),
            filterFn: (row, columnIds, filterValue) => getFilterValueForService(row, columnIds, filterValue, YES_NO_DROPDOWN_OPTIONS)
        },
        {header: "Closed Date", accessorKey: "closedDate"},
        {header: "Acv", accessorKey: "acv"},
        {
            header: "Duplicated Store", id: "duplicatedStore",
            accessorFn: (row) => row.duplicatedStore === 1 ? 1 : null,
            cell: ({getValue}) => getCellForService(getValue(), DUPLICATED_STORE_DROPDOWN_OPTIONS),
            filterFn: (row, columnId, filterValue) => !filterValue || _.find(DUPLICATED_STORE_DROPDOWN_OPTIONS, {value: row.getValue(columnId)}).label.toLowerCase().includes(filterValue.toLowerCase())
        },
    ];

    useEffect(() => {

        setDecodeTablesLoading(true)
        setError(false);

        Promise.all([
            Promise.resolve(loadSalesforceStores()).catch(() => undefined),
            makeApiCallWithoutCatch(`${process.env.REACT_APP_EDP_API_BASE_URL}/marketStores`),
            makeApiCallWithoutCatch(`${process.env.REACT_APP_EDP_API_BASE_URL}/marketsTwo/`),
        ])
            .then(([
                       _salesforceStoresLoadCache,
                       marketStoresResults,
                       marketsServiceTwo,
                   ]) => {
                const marketOptions = marketsServiceTwo?.data.map((market) => ({
                    value: market.marketId,
                    label: `${market.marketId} - ${market.description}`,
                    marketTag: market.marketTag
                }));
                const marketOptionsWithTags = marketsServiceTwo?.data.map((market) => ({
                    value: market.marketId,
                    label: `${market.marketTag} - ${market.description}`,
                    marketTag: market.marketTag
                }));
                setMarketsWithTags(marketOptionsWithTags)
                setMarkets(marketOptions)
                setMarketStores(marketStoresResults.data);
            })
            .catch(() => setError(true))
            .finally(() => setDecodeTablesLoading(false))
    }, []);

    function onRowEditConfirmedWrapper(newOrUpdatedRow) {
        const sanitizedData = setValuesToNullIfEmptyOrUndefined(newOrUpdatedRow);
        delete sanitizedData['markets']

        if (!newOrUpdatedRow.storeId) {
            updateQueryStringForState({sorting: JSON.stringify([{id: 'storeId', desc: true}])})
            updateQueryStringForState({filters: JSON.stringify([])})
            updateQueryStringForState({pageIndex: '0'})
        }

        return onRowEditConfirmed(sanitizedData);
    }

    function getMarketsForIndividualStore(row) {
        const filteredMarketStores = marketStores.filter(option => option.storeId === row.storeId);
        setCurrentMarketsForStore(filteredMarketStores)
        setCurrentStoreForMarket(row)
        setShowMarketsModal(true);
    }

    function marketsModalCancellationCallBack() {
        setCurrentMarketsForStore([])
        setCurrentStoreForMarket({})
        setShowMarketsModal(false);
    }

    function upsertMarketsForStore() {
        setShowLoadingModal(true);
        const marketIds = _.map(currentMarketsForStore, 'marketId');
        const storeId = currentStoreForMarket.storeId;
        makeApiCallPost(
            `${process.env.REACT_APP_EDP_API_BASE_URL}/marketsToStores/storeServiceUpsert`,
            {marketIds, storeId}
        )
            .then((response) => {
                if (response?.apiError) {
                    toastError(toast, `Failed to update storeId ${storeId} for markets ${marketIds} `);
                } else {
                    toastSuccess(toast, `Successfully added ${marketIds} to storeId ${storeId}`);
                    const filteredMarketStores = marketStores.filter(option => option.storeId !== storeId);
                    currentMarketsForStore.forEach(marketStore => {
                        filteredMarketStores.push({...marketStore, 'storeId': storeId});
                    })
                    setMarketStores(filteredMarketStores)
                    marketsModalCancellationCallBack()
                }
            }).finally(() => {
                setShowLoadingModal(false);
            });
    }

    const dbTagOptions = [
        {value: "EDW", label: "EDW"},
        {value: "LKAD", label: "LKAD"}
    ]

    const primarySvcOptions = [
        {value: "SA", label: "SA"},
        {value: "SN", label: "SN"},
        {value: "SG", label: "SG"},
        {value: "LKAD", label: "LKAD"}
    ]

    const xsMktOptions = [
        {value: 700, label: 700},
        {value: 701, label: 701}
    ]

    return (
        /*@formatter:off*/
        <Page breadCrumbs={<Breadcrumbs items={[{label: "Services"}, {label: "Store"}]}/>} beta name={"Store Service"}>
            <LoadingWrapper loading={loading || decodeTablesLoading}>
                {(error || data.apiError)&& <div className={'error-message'} data-testid={'error-div'}>Error loading data</div>}
                {(!error && data?.apiError === undefined) && <EditableReactTableV2
                    filterable
                    data={data.data}
                    columns={customColumns}
                    initialState={{
                        pagination: {pageSize: 25},
                        sorting: [{id: "retailerIdName", desc: false}, {id: "storeId", desc: false}]
                    }}
                    actionButtonsProps={{sticky: true}}
                    saveSortStateToUrl={true}
                    saveFilterStateToUrl={true}
                    savePageIndexToUrl={true}
                    customActionComponent={(row) =>
                        <button>
                            <i data-testid={"react-table-add-market-record-button"}
                               title={'Add Market'}
                               className={faIcons.market}
                               onClick={() => getMarketsForIndividualStore(row)}
                            />
                        </button>}
                    editModalAdditionalClass={"store-service-edit-modal"}
                    validatePreSave={({newData, rows, form}) => {
                        if (newData.closed === 1){
                            if (!newData.closedDate) {
                                newData.closedDate = moment(today).format('YYYY-MM-DD');
                            }
                        }
                        if (newData.closed === 0){
                            if (newData.closedDate) {
                                form.setError('closedDate', {type: 'custom', message: ['This is required to be empty because closed is marked as No']});
                                return false;
                            }
                        }
                        if (newData.closedDate !== null) {
                            if (new Date(newData.closedDate).getYear() >= 10000) {
                                form.setError('closedDate', {type: 'custom', message: ['Invalid year']});
                                return false;
                            }
                        }

                        if (newData.openDate !== null) {
                            if (new Date(newData.openDate).getYear() >= 10000) {
                                form.setError('openDate', {type: 'custom', message: ['Invalid year']});
                                return false;
                            }
                        }

                        if (newData.openDate && newData.closedDate) {
                            if(new Date(newData.openDate) >= new Date(newData.closedDate)) {
                                form.setError('openDate', {type: 'custom', message: ['Open date must be before closed date']});
                                return false;
                            }
                        }

                        return true;
                    }}
                    editTable={[
                        {id: "retailer", Component: args => <RetailerInputField {...args} label={"Retailer"}/>},
                        {id: "storeName", Component: args => <EditFormTextInputField{...args} label={"Store Name"} isRequired/>},
                        {id: "storeId", Component: args => <EditFormNumberInputField {...args} disabled={true} label={"Store Id"}/>},
                        {id: "storeTag", Component: args => <EditFormNumberInputField {...args} disabled={true} label={"Store Tag"}/>},
                        {id: "addr1", Component: args => <EditFormTextInputField{...args}  label={"Address 1"}/>},
                        {id: "addr2", Component: args => <EditFormTextInputField{...args}  label={"Address 2"}/>},
                        {id: "city", Component: args => <EditFormTextInputField{...args}  label={"City"}/>},
                        {id: "state", Component: args => <AddressStateInputField{...args}/>},
                        {id: "county", Component: args => <EditFormTextInputField{...args}  label={"County"}/>},
                        {id: "fips", Component: args => <EditFormTextInputField {...args} disabled label={"FIPS Code"}/>},
                        {id: "country", defaultValue: 'US', Component: args => <EditFormTextInputField{...args}  label={"Country"}/>},
                        {id: "fipsCounty", Component: args => <EditFormTextInputField {...args} disabled label={"FIPS County"}/>},
                        {id: "zip1", Component: args => <EditFormTextInputField{...args}  label={"Zipcode 1"}/>},
                        {id: "zip2", Component: args => <EditFormTextInputField{...args}  label={"Zipcode 2"}/>},
                        {id: "storeAddress", Component: args => <AddressMapInputField {...args} label={"Store Address"}/>},
                        {id: "latitude", defaultValue: 0, Component: args => <EditFormNumberInputField {...args} disabled label={"Latitude"}/>},
                        {id: "longitude", defaultValue: 0, Component: args => <EditFormNumberInputField {...args} disabled label={"Longitude"}/>},
                        {id: "storeCode", Component: args => <EditFormTextInputField{...args}  label={"Store Code"} isRequired/>},
                        {id: "externalStoreId", Component: args => <EditFormTextInputField{...args}  label={"External Store Id"}/>},
                        {id: "account", Component: args => <EditFormTextInputField{...args} disabled={true} label={"Account"}/>},
                        {id: "banner", Component: args => <EditFormTextInputField{...args} disabled={true} label={"Banner"}/>},
                        {id: "consumerName", Component: args => <EditFormTextInputField{...args}  label={"Consumer Name"} isRequired/>},
                        {id: "openDate", Component: args => <EditFormDateInputField{...args}  label={"Open Date"}/>},
                        {id: "closed", defaultValue: 0, Component: args => <EditFormDropdownInputField {...args} options={YES_NO_DROPDOWN_OPTIONS} label={"Closed"}/>},
                        {id: "closedDate", Component: args => <EditFormDateInputField{...args}  label={"Closed Date"}/>},
                        {id: "acv", Component: args => <EditFormNumberInputField {...args} label={"ACV"}/>},
                        {id: "areaCode", Component: args => <EditFormTextInputField{...args}  label={"Area Code"}/>},
                        {id: "dbTag", Component: args => <EditFormDropdownInputField{...args}  label={"DB Tag"} options={dbTagOptions} isRequired/>},
                        {id: "phone", Component: args => <EditFormTextInputField{...args}  label={"Phone"}/>},
                        {id: "primarySvc", Component: args => <EditFormDropdownInputField{...args}  label={"Primary Svc"} options={primarySvcOptions} isRequired/>},
                        {id: "version", Component: args => <EditFormNumberInputField {...args} label={"Version"}/>},
                        {id: "xsMkt", Component: args => <EditFormDropdownInputField {...args} label={"XS Mkt"} options={xsMktOptions} isRequired/>},
                        {id: "retailTag", Component: args => <EditFormNumberInputField {...args} disabled label={"Retail Tag"}/>},
                        {id: "markets", Component: args => <MarketStoreDropdownField  {...args} label={"Markets by Id"} marketStores={marketStores} markets={markets}/>},
                        {id: "marketTagsForMaryAndAmy", Component: args => <MarketStoreDropdownField  {...args} label={"Markets by Market Tag"} marketStores={marketStores} markets={marketsWithTags}/>},
                        {id: "duplicatedStore", defaultValue: null, Component: args => <EditFormDropdownInputField {...args} options={DUPLICATED_STORE_DROPDOWN_OPTIONS} label={"Duplicated Store"}/>}
                    ]}
                    getTrProps={(rowInfo) => {
                        let className = ''
                        const notActiveStore = getSalesforceStores()[rowInfo.original.storeId] || false;

                        if (notActiveStore) {
                            className = 'inactive-store-row'
                        }
                        return {className}
                    }}
                    onRowEditConfirmed={onRowEditConfirmedWrapper}
                    onRowDeleteConfirmed={onRowDeleteConfirmed}
                >
                    {
                        () => {
                            return (
                                <div>
                                    <div className={'market-service-content-controls'}>
                                        <InactiveRowKey inactiveColorClassName={'inactive-store-row'}/>
                                        <DownloadCsvFromBackendServiceButton endpoint={downloadEndpointUrl}
                                                                             fileName={'all-stores.csv'}/>
                                        <BulkUploadStoresButton onSubmit={onBulkRowUploadConfirmed} data={data.data}/>
                                    </div>
                                    {showMarketsModal && <ConfirmationModal
                                        modalClassName={"Modal market-stores-modal"}
                                        confirmButtonText={"Save"}
                                        headerText={`Edit Markets: ${currentStoreForMarket.storeName} - ${currentStoreForMarket.storeId}`}
                                        cancellationCallback={() => marketsModalCancellationCallBack()}
                                        confirmationCallback={() => upsertMarketsForStore()}
                                        loading={showLoadingModal}
                                    >
                                        <div data-testid={'market-stores-modal-content'} className={'market-stores-modal-content'} style={{height: '300px'}}>
                                            <SelectDropdown
                                                id={`markets_dropdown`}
                                                dataTestId={"markets_dropdown"}
                                                options={markets}
                                                onChange={(options) => {
                                                    if (options !== null) {
                                                        const mappedOptions = options.map(x => ({marketId: x?.value,}));
                                                        setCurrentMarketsForStore(mappedOptions);
                                                    } else {
                                                        setCurrentMarketsForStore([]);
                                                    }
                                                }}
                                                selectValues={_.map(currentMarketsForStore, 'marketId')}
                                                isDisabled={false}
                                                isMulti
                                                isClearable={true}
                                            />
                                        </div>
                                    </ConfirmationModal>}
                                </div>
                            )
                        }
                    }
                </EditableReactTableV2>}
            </LoadingWrapper>
        </Page>
        /*@formatter:on*/
    );
};

export default StoreService;
